import { useState } from 'react';
import {
  Epic,
  Panel,
  PanelHeader,
  Group,
  Header,
  SimpleCell,
  Button,
  Spinner,
  Tabbar,
  TabbarItem,
  Banner,
  Text,
} from '@vkontakte/vkui';
import { useNavigate } from 'react-router-dom';
import { copyText, openUrl } from '../utils/vkBridge';
import { useMe, useLicense, useSimulator, useMaps } from '../api/hooks';
import {
  STAFF_PREVIEW_LICENSE,
  STAFF_PREVIEW_PARTICIPANT,
} from '../constants/staffPreview';
import { Icon28UserOutline, Icon28SettingsOutline } from '@vkontakte/icons';

export function ProfilePage({
  isStaff,
  staffPreview = false,
}: {
  isStaff: boolean;
  staffPreview?: boolean;
}) {
  const { data: me } = useMe();
  const { data: license, isLoading: licLoading } = useLicense(!staffPreview);
  const { data: simulator } = useSimulator();
  const { data: mapsData } = useMaps();
  const [showPass, setShowPass] = useState(false);
  const [actionHint, setActionHint] = useState<string | null>(null);
  const navigate = useNavigate();

  const participant = staffPreview
    ? STAFF_PREVIEW_PARTICIPANT
    : me?.participant;
  const licenseData = staffPreview ? STAFF_PREVIEW_LICENSE : license;
  const licenseLoading = staffPreview ? false : licLoading;

  const tabbar = (
    <Tabbar>
      <TabbarItem selected label="Профиль" onClick={() => navigate('/profile')}>
        <Icon28UserOutline />
      </TabbarItem>
      {isStaff && (
        <TabbarItem label="Админ" onClick={() => navigate('/admin')}>
          <Icon28SettingsOutline />
        </TabbarItem>
      )}
    </Tabbar>
  );

  return (
    <Epic activeStory="profile" tabbar={tabbar}>
      <Panel id="profile">
        <PanelHeader>Профиль</PanelHeader>
      {staffPreview && (
        <Banner
          mode="tint"
          before={null}
          title="Демо-профиль участника"
          subtitle="Данные участника и лицензии — для предпросмотра. Симулятор и карты — реальные."
        />
      )}
      <Group header={<Header size="s">Участник</Header>}>
        <SimpleCell subtitle="ID Первых">{participant?.idPervye}</SimpleCell>
        <SimpleCell subtitle="Email">{participant?.email}</SimpleCell>
        <SimpleCell subtitle="ФИО">{participant?.fullName}</SimpleCell>
      </Group>

      <Group header={<Header size="s">Лицензия симулятора</Header>} className="pb-glow-card">
        {licenseLoading ? (
          <Spinner style={{ margin: 16 }} />
        ) : licenseData ? (
          <>
            <SimpleCell subtitle="Логин">{licenseData.login}</SimpleCell>
            <SimpleCell
              subtitle="Пароль"
              after={
                <Button size="s" mode="link" onClick={() => setShowPass(!showPass)}>
                  {showPass ? 'Скрыть' : 'Показать'}
                </Button>
              }
            >
              {showPass ? licenseData.password : '••••••••'}
            </SimpleCell>
            <SimpleCell>
              <Button
                size="m"
                stretched
                mode="secondary"
                onClick={async () => {
                  const ok = await copyText(licenseData.password);
                  setActionHint(ok ? 'Пароль скопирован' : 'Не удалось скопировать');
                  if (ok) setTimeout(() => setActionHint(null), 2500);
                }}
              >
                Копировать пароль
              </Button>
            </SimpleCell>
            {staffPreview && (
              <SimpleCell>
                <Text className="pb-text-secondary" style={{ fontSize: 13 }}>
                  Демо-логин не работает в симуляторе.
                </Text>
              </SimpleCell>
            )}
          </>
        ) : (
          <SimpleCell>Лицензия скоро будет выдана</SimpleCell>
        )}
      </Group>

      <Group header={<Header size="s">Скачать симулятор</Header>}>
        <SimpleCell subtitle={simulator?.version ?? '—'}>
          {simulator?.releasedAt
            ? `Релиз: ${new Date(simulator.releasedAt).toLocaleDateString('ru')}`
            : 'Версия не указана'}
        </SimpleCell>
        {simulator?.downloadUrl && (
          <SimpleCell>
            <Button
              size="l"
              stretched
              className="pb-gradient-btn"
              onClick={() => void openUrl(simulator.downloadUrl!)}
            >
              Скачать
            </Button>
          </SimpleCell>
        )}
      </Group>

      <Group header={<Header size="s">Карты</Header>}>
        {mapsData?.maps?.length ? (
          mapsData.maps.map((m) => (
            <SimpleCell
              key={m.id}
              subtitle={m.description ?? undefined}
              after={
                <Button
                  size="s"
                  onClick={() => void openUrl(m.downloadUrl)}
                >
                  Скачать
                </Button>
              }
            >
              {m.title}
            </SimpleCell>
          ))
        ) : (
          <SimpleCell>Карты скоро появятся</SimpleCell>
        )}
      </Group>

      {actionHint && (
        <Group>
          <SimpleCell>{actionHint}</SimpleCell>
        </Group>
      )}
      </Panel>
    </Epic>
  );
}
